/**
 * The interface level of the three-level editor (REQ-UI-141 / -142 / -144 /
 * -146; ADR-039 §D-1).
 *
 * Sits below the peer form in the same scrollable pane. Each interface is a row
 * that **expands in place** into its own editor — which itself contains the
 * connection level — so the containment the data model has (a connection belongs
 * to an interface belongs to a peer) is the containment the surface shows.
 *
 * # No sub-tabs (ADR-024 §4)
 *
 * Rows expand rather than switching a pane. Sub-tabs would hide two of the three
 * levels at all times, and this dialog is opened as often to *stop* something as
 * to configure it — a kill switch two clicks behind a tab bar is one an operator
 * hesitates over.
 *
 * # One expanded at a time
 *
 * Not a fashion: two open interface editors are two independent unsaved drafts
 * of live egress configuration, and the operator would have no way to tell which
 * Save belonged to which without reading the whole pane.
 *
 * Cross-references:
 *   - ADR-024 §4; ADR-039 §D-1; feature-spec §3.4 / §5.1.
 *   - REQ-UI-141 (one pane, sticky headers), -142 (mutation at each level),
 *     -144 (ancestor annotation), -146 (reserved inbound read-only).
 */
import { findInterfaceType, isReservedType, type InterfaceState } from "@/peers/peer-catalog";
import { resolveEffectiveState, type ChainLink } from "@/peers/peer-effective-state";
import { PeerInterfaceEditor } from "@/peers/peer-interface-editor";
import { PeerInterfaceSummary } from "@/peers/peer-interface-summary";
import { PeerSection } from "@/peers/peer-section";
import { StatePill } from "@/peers/peer-state-controls";
import { PeerSuppressionNote } from "@/peers/peer-suppression-note";
import {
  isPrivilegedView,
  toInterfaceConfig,
  type PeerInterfaceConfig,
  type PeerInterfaceView,
} from "@/peers/peer-model";
import { cn } from "@/lib/utils";

/** Props for {@link PeerInterfacesSection}. */
export interface PeerInterfacesSectionProps {
  /** The owning peer's name and state — the ancestor chain's only link here. */
  peer: ChainLink;
  interfaces: readonly PeerInterfaceView[];
  isLoading: boolean;
  error: string | null;
  /** The expanded interface, or `"new"` for an unsaved draft, or `null`. */
  expandedId: string | null;
  onExpand: (interfaceId: string | null) => void;
  mayMutate: boolean;
  /** Renders the create affordance and the draft row. */
  onCreate: () => void;
  /** The unsaved draft, when one is being created. */
  draft: PeerInterfaceConfig | null;
  onSave: (config: PeerInterfaceConfig) => void;
  onDelete: (interfaceId: string) => void;
  onCancelDraft: () => void;
  onSetInterfaceState: (interfaceId: string, state: InterfaceState) => void;
  onSetConnectionState: (interfaceId: string, connectionId: string, state: InterfaceState) => void;
  /** Delete one persisted connection on the server (REQ-PEER-105 / REQ-UI-154). */
  onDeleteConnection: (interfaceId: string, connectionId: string) => void;
  savePending?: boolean;
  statePending?: boolean;
  saveError?: string | null;
  /** Mints ids for new connections; injectable so tests are deterministic. */
  makeId?: () => string;
}

/** Render the interfaces level and, expanded within it, the connection level. */
export function PeerInterfacesSection({
  peer,
  interfaces,
  isLoading,
  error,
  expandedId,
  onExpand,
  mayMutate,
  onCreate,
  draft,
  onSave,
  onDelete,
  onCancelDraft,
  onSetInterfaceState,
  onSetConnectionState,
  onDeleteConnection,
  savePending = false,
  statePending = false,
  saveError,
  makeId,
}: PeerInterfacesSectionProps): React.ReactElement {
  const ancestors: readonly ChainLink[] = [peer];
  return (
    <PeerSection
      id="interfaces"
      title="Interfaces"
      action={
        mayMutate && (
          <button
            type="button"
            onClick={onCreate}
            data-testid="peer-add-interface"
            className="rounded border border-border px-2 py-0.5 text-meta text-text-primary hover:bg-bg-elevated"
          >
            Add interface
          </button>
        )
      }
    >
      {isLoading ? (
        <p className="text-body-sm text-text-secondary">Loading interfaces…</p>
      ) : error !== null ? (
        <p role="alert" className="text-body-sm text-destructive">
          Could not load this peer&rsquo;s interfaces: {error}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {interfaces.length === 0 && draft === null && (
            <p className="text-body-sm text-text-muted" data-testid="peer-interfaces-empty">
              This peer has no interfaces, so nothing is reaching it.
            </p>
          )}
          {interfaces.map((view) => (
            <InterfaceRow
              key={view.interface_id}
              view={view}
              ancestors={ancestors}
              expanded={expandedId === view.interface_id}
              onToggle={() => onExpand(expandedId === view.interface_id ? null : view.interface_id)}
              mayMutate={mayMutate}
              onSave={onSave}
              onDelete={() => onDelete(view.interface_id)}
              onCancel={() => onExpand(null)}
              otherNames={interfaces
                .filter((other) => other.interface_id !== view.interface_id)
                .map((other) => other.name)}
              onSetState={(state) => onSetInterfaceState(view.interface_id, state)}
              onSetConnectionState={(connectionId, state) =>
                onSetConnectionState(view.interface_id, connectionId, state)
              }
              onDeleteConnection={(connectionId) =>
                onDeleteConnection(view.interface_id, connectionId)
              }
              savePending={savePending}
              statePending={statePending}
              saveError={saveError}
              makeId={makeId}
            />
          ))}
          {draft !== null && (
            <div
              data-testid="peer-interface-draft"
              className="rounded border border-brand bg-bg-base"
            >
              <div className="border-b border-border px-3 py-2 text-body-sm text-text-primary">
                New interface (unsaved)
              </div>
              <PeerInterfaceEditor
                key={draft.interface_id}
                value={draft}
                isNew
                otherNames={interfaces.map((other) => other.name)}
                makeId={makeId}
                savePending={savePending}
                statePending={statePending}
                saveError={saveError}
                onSave={onSave}
                onDelete={onCancelDraft}
                onCancel={onCancelDraft}
                onSetState={() => undefined}
                onSetConnectionState={() => undefined}
              />
            </div>
          )}
        </div>
      )}
    </PeerSection>
  );
}

/** One interface: a summary row that expands into its editor or its read view. */
function InterfaceRow({
  view,
  ancestors,
  expanded,
  onToggle,
  mayMutate,
  onSave,
  onDelete,
  onCancel,
  otherNames,
  onSetState,
  onSetConnectionState,
  onDeleteConnection,
  savePending,
  statePending,
  saveError,
  makeId,
}: {
  view: PeerInterfaceView;
  ancestors: readonly ChainLink[];
  expanded: boolean;
  onToggle: () => void;
  mayMutate: boolean;
  onSave: (config: PeerInterfaceConfig) => void;
  onDelete: () => void;
  onCancel: () => void;
  otherNames: readonly string[];
  onSetState: (state: InterfaceState) => void;
  onSetConnectionState: (connectionId: string, state: InterfaceState) => void;
  onDeleteConnection: (connectionId: string) => void;
  savePending?: boolean;
  statePending?: boolean;
  saveError?: string | null;
  makeId?: () => string;
}): React.ReactElement {
  const descriptor = findInterfaceType(view.type_id);
  const reserved = isReservedType(view.type_id);
  const resolved = resolveEffectiveState(ancestors, view.state);
  // Editable only when the session may mutate, the response carried the fields an
  // editor is made of, and the type is one this build can actually realize. Each
  // of the three is a separate reason and none implies the others.
  const editable = mayMutate && isPrivilegedView(view) && !reserved;
  const bodyId = `peer-interface-body-${view.interface_id}`;

  return (
    <section
      data-testid={`peer-interface-row-${view.name}`}
      className={cn("rounded border bg-bg-base", expanded ? "border-brand" : "border-border")}
    >
      <h4>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={bodyId}
          data-testid={`peer-interface-toggle-${view.name}`}
          className="flex w-full flex-wrap items-center gap-2 px-3 py-2 text-left hover:bg-bg-elevated"
        >
          <span aria-hidden="true" className="text-meta text-text-secondary">
            {expanded ? "▾" : "▸"}
          </span>
          <span className="min-w-0 flex-1 truncate text-body-sm text-text-primary">
            {view.name}
          </span>
          {reserved && (
            <span
              data-testid={`peer-interface-reserved-${view.name}`}
              className="rounded border border-border px-1.5 py-0.5 text-meta text-text-secondary"
            >
              Reserved — not implemented
            </span>
          )}
          <StatePill state={view.state} />
          <PeerSuppressionNote resolved={resolved} testId={`interface-${view.name}`} />
        </button>
      </h4>
      <p className="px-3 pb-2 text-meta text-text-secondary">
        {descriptor?.label ?? view.type_id} · {view.connections.length}{" "}
        {view.connections.length === 1 ? "connection" : "connections"}
        {view.connections.length > 0 &&
          ` · ${view.connections.filter((c) => c.state === "started").length} started`}
      </p>
      {expanded && (
        <div id={bodyId} className="border-t border-border">
          {reserved && (
            <p
              className="px-4 py-3 text-body-sm text-text-muted"
              data-testid={`peer-interface-reserved-note-${view.name}`}
            >
              This interface type is registered so it can be configured and recognized, but Meridian
              has no implementation for it. It cannot be started, and its configuration is shown
              read-only.
            </p>
          )}
          {editable ? (
            <PeerInterfaceEditor
              value={toInterfaceConfig(view)}
              isNew={false}
              otherNames={otherNames}
              makeId={makeId}
              savePending={savePending}
              statePending={statePending}
              saveError={saveError}
              ancestors={ancestors}
              onSave={onSave}
              onDelete={onDelete}
              onCancel={onCancel}
              onSetState={onSetState}
              onSetConnectionState={onSetConnectionState}
              onDeleteConnection={onDeleteConnection}
            />
          ) : (
            <PeerInterfaceSummary view={view} ancestors={ancestors} />
          )}
        </div>
      )}
    </section>
  );
}
